import type { BoardMemberRecord } from '../types';
import { getSiteAuthRuntime, getStoredToken } from './bliss-app-auth';

export interface UploadedBoardMemberPhoto {
  url: string;
  storageKey: string;
  name: string;
  contentType: string;
  sizeBytes: number;
  uploadedAt?: string | null;
}

function getErrorMessage(payload: unknown, fallback: string): string {
  if (!payload || typeof payload !== 'object') {
    return fallback;
  }

  const source = payload as {
    error?: string | { message?: string };
    message?: string;
    data?: { message?: string };
  };

  if (typeof source.error === 'string' && source.error) {
    return source.error;
  }

  if (source.error && typeof source.error === 'object' && typeof source.error.message === 'string') {
    return source.error.message;
  }

  if (typeof source.message === 'string' && source.message) {
    return source.message;
  }

  if (source.data && typeof source.data.message === 'string' && source.data.message) {
    return source.data.message;
  }

  return fallback;
}

async function readResponseJson(response: Response): Promise<unknown> {
  return response.json().catch(() => null);
}

async function requestBoardMembersApi<T>(
  path: string,
  options: RequestInit = {},
  requireAuth = false,
): Promise<T> {
  const runtime = await getSiteAuthRuntime();
  const token = getStoredToken();
  const headers = new Headers(options.headers);
  const method = (options.method || 'GET').toUpperCase();
  const hasBody = options.body != null;

  if (requireAuth && !token) {
    throw new Error('You need to sign in again before changing board member profiles.');
  }

  if (hasBody && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(`${runtime.apiBaseUrl}${path}`, {
    ...options,
    method,
    headers,
  });

  const payload = await readResponseJson(response);
  if (!response.ok) {
    throw new Error(getErrorMessage(payload, 'The board member workspace could not reach the Bliss app API.'));
  }

  if (payload == null) {
    throw new Error('The board member workspace received an unexpected response from the Bliss app API.');
  }

  const source = payload as { data?: T };
  return (source.data ?? payload) as T;
}

async function requestBoardMembersUploadApi<T>(path: string, formData: FormData): Promise<T> {
  const runtime = await getSiteAuthRuntime();
  const token = getStoredToken();

  if (!token) {
    throw new Error('You need to sign in again before uploading board photos.');
  }

  const response = await fetch(`${runtime.apiBaseUrl}${path}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  const payload = await readResponseJson(response);
  if (!response.ok) {
    throw new Error(getErrorMessage(payload, 'The board member photo could not be uploaded right now.'));
  }

  const source = payload as { data?: T };
  return (source.data ?? payload) as T;
}

export async function listBlissBoardMembers(includeAll = false): Promise<BoardMemberRecord[]> {
  const query = includeAll ? '?include=all' : '';
  const result = await requestBoardMembersApi<{ members: BoardMemberRecord[] }>(
    `/board-members${query}`,
    {},
    includeAll,
  );
  return Array.isArray(result.members) ? result.members : [];
}

export async function createBlissBoardMember(member: BoardMemberRecord): Promise<BoardMemberRecord> {
  const result = await requestBoardMembersApi<{ member: BoardMemberRecord }>(
    '/board-members',
    {
      method: 'POST',
      body: JSON.stringify({ member }),
    },
    true,
  );
  return result.member;
}

export async function updateBlissBoardMember(member: BoardMemberRecord): Promise<BoardMemberRecord> {
  const result = await requestBoardMembersApi<{ member: BoardMemberRecord }>(
    `/board-members/${encodeURIComponent(member.id)}`,
    {
      method: 'PUT',
      body: JSON.stringify({ member }),
    },
    true,
  );
  return result.member;
}

export async function deleteBlissBoardMember(memberId: string): Promise<void> {
  await requestBoardMembersApi(
    `/board-members/${encodeURIComponent(memberId)}`,
    {
      method: 'DELETE',
    },
    true,
  );
}

export async function uploadBlissBoardMemberPhoto(
  memberId: string,
  file: File,
): Promise<UploadedBoardMemberPhoto> {
  const formData = new FormData();
  formData.append('board_member_id', memberId);
  formData.append('file', file);

  const result = await requestBoardMembersUploadApi<{ image: UploadedBoardMemberPhoto }>(
    '/board-members/upload',
    formData,
  );
  return result.image;
}
